import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import complaintService from '../../services/complaintService';
import Toast from '../../components/Toast';
import { getErrorMessage } from '../../utils/apiErrorHandler';
import { ConfirmDialog, FilePreviewDialog } from '../../components/dialogs';
import { obfuscateEmail } from '../../utils/emailObfuscation';
import config from '../../config/environment';

// Helper function to construct full file URL from relative path
const getFileUrl = (filePath) => {
  if (!filePath) return '';

  // If already a full URL, return as is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }

  // Get base URL without /api suffix
  const baseURL = config.api.baseURL.replace('/api', '');

  // Construct full URL - Laravel typically serves files from /storage/
  // Remove leading slash if present to avoid double slashes
  const cleanPath = filePath.startsWith('/') ? filePath.substring(1) : filePath;

  // Construct URL: baseURL/storage/filePath
  return `${baseURL}/storage/${cleanPath}`;
};

const ComplaintHistoryView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const [complaintId, setComplaintId] = useState('');
  const [loading, setLoading] = useState(true);
  const [statusResult, setStatusResult] = useState(null);
  const [error, setError] = useState(null);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'error'
  });
  const [previewDialog, setPreviewDialog] = useState({
    isOpen: false,
    fileUrl: '',
    fileName: '',
    fileType: ''
  });

  // Get complaint ID from query parameter
  useEffect(() => {
    const complaintParam = searchParams.get('complaint_id') || searchParams.get('complaintId') || searchParams.get('id');
    if (complaintParam) {
      setComplaintId(complaintParam);
      fetchComplaintHistory(complaintParam);
    } else {
      setError('Complaint ID is required in query parameter');
      setLoading(false);
    }
  }, [searchParams]);

  const transformHistoryToStatusUpdates = (history) => {
    if (!Array.isArray(history)) return [];

    return history.map((item) => {
      const date = item.updated_at
        ? new Date(item.updated_at).toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
        : 'N/A';

      let status = item.status || item.new_value || 'N/A';
      if (item.action_type === 'status_change' && item.new_value) {
        status = item.new_value;
      }

      let isRichContent = false;
      if (typeof status === 'string' && status.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(status);
          const changes = [];

          Object.keys(parsed).forEach(key => {
            if (parsed[key] !== null && parsed[key] !== undefined && parsed[key] !== '') {
              // Format key: department_name -> Department Name
              const formattedKey = key
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
              changes.push(`${formattedKey}: ${parsed[key]}`);
            }
          });

          if (changes.length > 0) {
            status = changes.join(' | ');
            isRichContent = true;
          }
        } catch (e) {
          // ignore
        }
      }

      const updatedBy = item.action_by?.role_name || 'System';
      const comment = item.remark || 'No remarks provided';

      return {
        date,
        comment,
        status,
        isRichContent,
        updatedBy
      };

      return {
        date,
        comment,
        status,
        updatedBy
      };
    });
  };

  const fetchComplaintHistory = async (complaintIdParam) => {
    if (!complaintIdParam) {
      setError('Complaint ID is required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch history and complaint details in parallel
      const [historyResponse, complaintsResponse] = await Promise.all([
        complaintService.getUserComplaintHistoryLogs(complaintIdParam),
        complaintService.getUserOnlineComplaints()
      ]);

      if (historyResponse.success && historyResponse.data) {
        const historyData = historyResponse.data;
        const history = historyData.history || [];
        const userDetails = historyData.user_details || {};

        // Find complaint in the list to get files and specific details
        let complaintDetails = null;
        if (complaintsResponse.success) {
          const complaints = complaintsResponse.data?.data || complaintsResponse.data || [];
          if (Array.isArray(complaints)) {
            complaintDetails = complaints.find(c =>
              (c.complaint_no === complaintIdParam) ||
              (c.complaint_number === complaintIdParam) ||
              (c.id === complaintIdParam) ||
              (c.complaint_id === complaintIdParam)
            );
          }
        }

        // Transform API response to match component's expected format
        const latestEntry = history.length > 0 ? history[0] : null;
        const statusUpdates = transformHistoryToStatusUpdates(history);

        // Use current_status from API response
        const currentStatus = historyData.current_status || (complaintDetails?.status || complaintDetails?.complaint_status || 'Pending');

        const submittedDateValue = complaintDetails?.submitted_date
          ? new Date(complaintDetails.submitted_date).toLocaleDateString('en-IN')
          : (history.length > 0
            ? new Date(history[history.length - 1].updated_at).toLocaleDateString('en-IN')
            : 'N/A');

        setStatusResult({
          complaintNumber: historyData.complaint_id || complaintIdParam,
          registeredBy: user?.name || userDetails.name || complaintDetails?.name || 'N/A',
          mobileNumber: user?.phone || user?.mobile || userDetails.mobile || userDetails.phone || complaintDetails?.mobile || 'N/A',
          email: user?.email || userDetails.email || complaintDetails?.email || 'N/A',
          status: currentStatus,
          submittedDate: submittedDateValue,
          lastUpdated: latestEntry?.updated_at
            ? new Date(latestEntry.updated_at).toLocaleDateString('en-IN')
            : 'N/A',
          statusUpdates: statusUpdates,
          documents: complaintDetails?.files || historyData.files || []
        });

        setToast({
          isVisible: true,
          message: 'Complaint history loaded successfully',
          type: 'success'
        });
      } else {
        const errorMessage = historyResponse.message || 'Failed to fetch complaint history';
        setError(errorMessage);
        setToast({
          isVisible: true,
          message: errorMessage,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error fetching complaint history:', error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage || 'An error occurred while fetching complaint history');
      setToast({
        isVisible: true,
        message: errorMessage || 'An error occurred. Please try again.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (complaintId) {
      fetchComplaintHistory(complaintId);
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutDialog(false);
    await logout();
    navigate('/');
  };

  const handleLogoutCancel = () => {
    setShowLogoutDialog(false);
  };

  const handleCreateComplaint = () => {
    navigate('/vigilance-dashboard/create');
  };

  const handlePreviewFile = (url, name) => {
    setPreviewDialog({
      isOpen: true,
      fileUrl: url,
      fileName: name,
      fileType: ''
    });
  };

  const getStatusColor = (status) => {
    if (!status) return 'bg-neutral-100 text-neutral-800';

    const statusLower = (status || '').toLowerCase();
    if (statusLower.includes('under review') || statusLower.includes('investigation')) {
      return 'bg-primary-100 text-primary-800';
    } else if (statusLower.includes('pending')) {
      return 'bg-yellow-100 text-yellow-800';
    } else if (statusLower.includes('approved') || statusLower.includes('resolved')) {
      return 'bg-secondary-100 text-secondary-800';
    } else if (statusLower.includes('rejected')) {
      return 'bg-red-100 text-red-800';
    } else if (statusLower.includes('closed')) {
      return 'bg-neutral-100 text-neutral-800';
    }
    return 'bg-neutral-100 text-neutral-800';
  };

  // Check if this is being rendered as a nested route (has parent layout)
  const isNestedRouteLoading = location.pathname.startsWith('/vigilance-dashboard/complaint-history');
  const renderHeaderLoading = !isNestedRouteLoading;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header - Only for standalone route */}
        {renderHeaderLoading && (
          <div className="bg-white shadow-sm border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                <div className="flex items-center">
                  <img
                    src={`${process.env.PUBLIC_URL}/logo192.png`}
                    alt="SJVN Limited Logo"
                    className="w-10 h-12 mr-3"
                    role="img"
                    aria-label="SJVN Limited Logo"
                  />
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">SJVN Limited</h2>
                    <p className="text-xs text-gray-500">Vigilance Portal</p>
                  </div>
                </div>
                <nav className="flex space-x-8">
                  <button
                    onClick={() => navigate('/vigilance-dashboard')}
                    className="px-3 py-2 text-sm font-medium transition-colors duration-200 text-gray-500 hover:text-gray-700"
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={() => navigate('/vigilance-dashboard/complaints')}
                    className="px-3 py-2 text-sm font-medium transition-colors duration-200 text-gray-500 hover:text-gray-700"
                  >
                    All Complaints
                  </button>
                  <button
                    onClick={handleCreateComplaint}
                    className="px-3 py-2 text-sm font-medium transition-colors duration-200 text-gray-500 hover:text-gray-700"
                  >
                    Create New Vigilance
                  </button>
                </nav>
                <div className="flex items-center space-x-4">
                  {user && (
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {user.name || 'User'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {user.email ? obfuscateEmail(user.email) : user.phone}
                      </p>
                    </div>
                  )}
                  <button
                    onClick={handleLogoutClick}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors duration-300"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-12 text-center">
              <div className="flex flex-col items-center">
                <svg className="animate-spin h-8 w-8 text-primary-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-gray-600">Loading complaint history...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if this is being rendered as a nested route (has parent layout)
  const isNestedRouteError = location.pathname.startsWith('/vigilance-dashboard/complaint-history');
  const renderHeaderError = !isNestedRouteError;

  if (error && !statusResult) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header - Only for standalone route */}
        {renderHeaderError && (
          <div className="bg-white shadow-sm border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                <div className="flex items-center">
                  <img
                    src={`${process.env.PUBLIC_URL}/logo192.png`}
                    alt="SJVN Limited Logo"
                    className="w-10 h-12 mr-3"
                    role="img"
                    aria-label="SJVN Limited Logo"
                  />
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">SJVN Limited</h2>
                    <p className="text-xs text-gray-500">Vigilance Portal</p>
                  </div>
                </div>
                <nav className="flex space-x-8">
                  <button
                    onClick={() => navigate('/vigilance-dashboard')}
                    className="px-3 py-2 text-sm font-medium transition-colors duration-200 text-gray-500 hover:text-gray-700"
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={() => navigate('/vigilance-dashboard/complaints')}
                    className="px-3 py-2 text-sm font-medium transition-colors duration-200 text-gray-500 hover:text-gray-700"
                  >
                    All Complaints
                  </button>
                  <button
                    onClick={handleCreateComplaint}
                    className="px-3 py-2 text-sm font-medium transition-colors duration-200 text-gray-500 hover:text-gray-700"
                  >
                    Create New Vigilance
                  </button>
                </nav>
                <div className="flex items-center space-x-4">
                  {user && (
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {user.name || 'User'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {user.email ? obfuscateEmail(user.email) : user.phone}
                      </p>
                    </div>
                  )}
                  <button
                    onClick={handleLogoutClick}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors duration-300"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-12 text-center">
              <div className="flex flex-col items-center">
                <svg className="w-12 h-12 text-red-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-lg font-medium text-gray-900 mb-2">Error loading complaint history</p>
                <p className="text-gray-500 mb-4">{error}</p>
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={handleRefresh}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Retry
                  </button>
                  <button
                    onClick={() => navigate('/vigilance-dashboard/complaints')}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Back to Complaints
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if this is being rendered as a nested route (has parent layout)
  const isNestedRoute = location.pathname.startsWith('/vigilance-dashboard/complaint-history');

  // Only show header if it's the standalone route
  const renderHeader = !isNestedRoute;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Only for standalone route */}
      {renderHeader && (
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Left Side - SJVN Branding */}
              <div className="flex items-center">
                <img
                  src={`${process.env.PUBLIC_URL}/logo192.png`}
                  alt="SJVN Limited Logo"
                  className="w-10 h-12 mr-3"
                  role="img"
                  aria-label="SJVN Limited Logo"
                />
                <div>
                  <h2 className="text-lg font-bold text-gray-900">SJVN Limited</h2>
                  <p className="text-xs text-gray-500">Vigilance Portal</p>
                </div>
              </div>

              {/* Center - Navigation */}
              <nav className="flex space-x-8">
                <button
                  onClick={() => navigate('/vigilance-dashboard')}
                  className="px-3 py-2 text-sm font-medium transition-colors duration-200 text-gray-500 hover:text-gray-700"
                >
                  Dashboard
                </button>
                <button
                  onClick={() => navigate('/vigilance-dashboard/complaints')}
                  className="px-3 py-2 text-sm font-medium transition-colors duration-200 text-gray-500 hover:text-gray-700"
                >
                  All Complaints
                </button>
                <button
                  onClick={handleCreateComplaint}
                  className="px-3 py-2 text-sm font-medium transition-colors duration-200 text-gray-500 hover:text-gray-700"
                >
                  Create New Vigilance
                </button>
              </nav>

              {/* Right Side - User Info and Logout */}
              <div className="flex items-center space-x-4">
                {user && (
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {user.name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {user.email || user.phone}
                    </p>
                  </div>
                )}
                <button
                  onClick={handleLogoutClick}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors duration-300"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Complaint History</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Complaint Number: <span className="font-mono font-semibold">{statusResult?.complaintNumber || complaintId}</span>
                </p>
              </div>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors duration-300 focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 ${loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                aria-label="Refresh complaint history"
              >
                <svg
                  className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>

          {statusResult && (
            <div className="p-6 space-y-6">
              {/* Complaint Details Section */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg className="w-5 h-5 text-primary-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Complaint Details
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Registered By:</label>
                    <p className="text-gray-900 font-medium">{statusResult.registeredBy}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Mobile Number:</label>
                    <p className="text-gray-900 font-medium">{statusResult.mobileNumber}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Email:</label>
                    <p className="text-gray-900 font-medium">{obfuscateEmail(statusResult.email)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Current Status:</label><br />
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full mt-1 ${getStatusColor(statusResult.status)}`}>
                      {statusResult.status}
                    </span>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Submitted Date:</label>
                    <p className="text-gray-900 font-medium">{statusResult.submittedDate}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Last Updated:</label>
                    <p className="text-gray-900 font-medium">{statusResult.lastUpdated}</p>
                  </div>
                </div>
              </div>

              {/* Documents Section */}
              {statusResult.documents && statusResult.documents.length > 0 && (() => {
                const supportingDocs = statusResult.documents.filter(doc =>
                  doc.file_type === 'supporting' ||
                  (typeof doc !== 'string' && !doc.file_type)
                );
                const generalDocs = statusResult.documents.filter(doc => doc.file_type === 'general');

                const renderFileList = (files, title) => {
                  if (!files || files.length === 0) return null;
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <svg className="w-5 h-5 text-primary-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        {title}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {files.map((file, index) => {
                          const filePath = typeof file === 'string' ? file : (file.file_path || file.path || file.name);
                          const getFileNameFromPath = (path) => {
                            if (!path) return 'Document';
                            return path.split('/').pop() || 'Document';
                          };
                          const fileName = typeof file === 'string' ? file : (file.name || file.original_name || getFileNameFromPath(file.file_type ? `${file.file_type}-${getFileNameFromPath(filePath)}` : filePath));
                          const fileUrl = getFileUrl(filePath);

                          return (
                            <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-100 hover:shadow-sm transition-shadow duration-200">
                              <div className="flex items-center min-w-0 flex-1 mr-2">
                                <svg className="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="text-sm text-gray-700 truncate" title={fileName}>{fileName}</span>
                              </div>
                              <button
                                onClick={() => handlePreviewFile(fileUrl, fileName)}
                                className="text-primary-600 hover:text-primary-700 p-1.5 hover:bg-white rounded-full transition-colors flex-shrink-0"
                                aria-label="View document"
                                title="View Document"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                };

                return (
                  <div className="space-y-6">
                    {renderFileList(supportingDocs, 'Supporting Documents')}
                    {renderFileList(generalDocs, 'General Documents')}
                  </div>
                );
              })()}

              {/* Status Timeline Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg className="w-5 h-5 text-primary-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Status Updates
                </h4>

                {statusResult.statusUpdates && statusResult.statusUpdates.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Comment from Authority</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Status/Comment</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Updated By</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {statusResult.statusUpdates.map((update, index) => (
                          <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                            <td className="px-4 py-4 text-sm text-gray-900 font-medium">
                              {update.date}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-700">
                              {update.comment}
                            </td>
                            <td className="px-4 py-4">
                              {update.isRichContent ? (
                                <span className="text-sm text-gray-700 block whitespace-pre-wrap">
                                  {update.status}
                                </span>
                              ) : (
                                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(update.status)}`}>
                                  {update.status}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-600">
                              {update.updatedBy}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No status updates available yet.</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={() => navigate('/vigilance-dashboard/complaints')}
                  className="flex-1 bg-primary-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-700 transition-all duration-300 focus:ring-2 focus:ring-primary-400 focus:ring-offset-2"
                >
                  Back to All Complaints
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      {renderHeader && (
        <ConfirmDialog
          isOpen={showLogoutDialog}
          title="Confirm Logout"
          message="Are you sure you want to logout? You will need to login again to access your dashboard."
          onConfirm={handleLogoutConfirm}
          onCancel={handleLogoutCancel}
          confirmText="Logout"
          cancelText="Cancel"
        />
      )}

      {/* Toast Notification */}
      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ isVisible: false, message: '', type: 'error' })}
      />
      <FilePreviewDialog
        isOpen={previewDialog.isOpen}
        onClose={() => setPreviewDialog({ ...previewDialog, isOpen: false })}
        fileUrl={previewDialog.fileUrl}
        fileName={previewDialog.fileName}
        fileType={previewDialog.fileType}
      />
    </div>
  );
};

export default ComplaintHistoryView;

